/**
 * Payment Context
 *
 * Provides payment state management across the application.
 * Handles transaction creation, verification, and payment history.
 */

import React, { createContext, useContext, useReducer, useCallback, useMemo, ReactNode } from 'react';
import paymentService from '../services/paymentService';
import type {
  PaymentContextType,
  Payment,
  CreateTransactionRequest,
  TransactionReference,
} from '../types/payment';

// Payment Context
const PaymentContext = createContext<PaymentContextType | null>(null);

// Payment State Interface
interface PaymentState {
  currentPayment: Payment | null;
  paymentHistory: Payment[];
  loading: boolean;
  error: string | null;
}

// Payment Actions
type PaymentAction =
  | { type: 'SET_LOADING'; payload: boolean }
  | { type: 'SET_ERROR'; payload: string | null }
  | { type: 'SET_CURRENT_PAYMENT'; payload: Payment | null }
  | { type: 'SET_PAYMENT_HISTORY'; payload: Payment[] }
  | { type: 'CLEAR_ERROR' }
  | { type: 'RESET_PAYMENT' };

// Payment Reducer
const paymentReducer = (state: PaymentState, action: PaymentAction): PaymentState => {
  switch (action.type) {
    case 'SET_LOADING':
      return { ...state, loading: action.payload };

    case 'SET_ERROR':
      return { ...state, error: action.payload, loading: false };

    case 'SET_CURRENT_PAYMENT':
      return { ...state, currentPayment: action.payload, loading: false, error: null };

    case 'SET_PAYMENT_HISTORY':
      return { ...state, paymentHistory: action.payload, loading: false };

    case 'CLEAR_ERROR':
      return { ...state, error: null };

    case 'RESET_PAYMENT':
      return { ...state, currentPayment: null, error: null };

    default:
      return state;
  }
};

// Initial State
const initialState: PaymentState = {
  currentPayment: null,
  paymentHistory: [],
  loading: false,
  error: null,
};

// Provider Props
interface PaymentProviderProps {
  children: ReactNode;
}

/**
 * Payment Provider Component
 * Wraps the application to provide payment context
 */
export const PaymentProvider: React.FC<PaymentProviderProps> = ({ children }) => {
  const [state, dispatch] = useReducer(paymentReducer, initialState);

  /**
   * Create a new transaction
   * Creates a payment record and returns transaction reference for Wompi Widget
   */
  const createTransaction = useCallback(
    async (data: CreateTransactionRequest): Promise<TransactionReference | null> => {
      try {
        // Clear any previous errors
        dispatch({ type: 'CLEAR_ERROR' });
        dispatch({ type: 'SET_LOADING', payload: true });

        // Validate data before sending
        const validation = paymentService.validateTransactionData(data);
        if (!validation.valid) {
          throw new Error(validation.errors.join(', '));
        }

        // Create transaction
        const transactionRef = await paymentService.createTransaction(data);

        // We don't set current payment here because it's created in backend
        // It will be set when we verify the transaction after payment
        dispatch({ type: 'SET_LOADING', payload: false });

        return transactionRef;
      } catch (error) {
        const errorMessage = error instanceof Error ? error.message : 'Failed to create transaction';
        console.error('Error creating transaction:', error);
        dispatch({ type: 'SET_ERROR', payload: errorMessage });
        return null;
      }
    },
    []
  );

  /**
   * Verify transaction status
   * Checks the current status of a transaction and updates current payment
   */
  const verifyTransaction = useCallback(async (reference: string): Promise<Payment | null> => {
    try {
      dispatch({ type: 'CLEAR_ERROR' });
      dispatch({ type: 'SET_LOADING', payload: true });

      const payment = await paymentService.verifyTransaction(reference);

      if (payment) {
        dispatch({ type: 'SET_CURRENT_PAYMENT', payload: payment });
      } else {
        dispatch({ type: 'SET_LOADING', payload: false });
      }

      return payment;
    } catch (error) {
      const errorMessage = error instanceof Error ? error.message : 'Failed to verify transaction';
      console.error('Error verifying transaction:', error);
      dispatch({ type: 'SET_ERROR', payload: errorMessage });
      return null;
    }
  }, []);

  /**
   * Fetch payment history for the authenticated user
   */
  const fetchPaymentHistory = useCallback(async (): Promise<void> => {
    try {
      dispatch({ type: 'CLEAR_ERROR' });
      dispatch({ type: 'SET_LOADING', payload: true });

      const response = await paymentService.getPaymentHistory(1, 50); // Get last 50 payments
      dispatch({ type: 'SET_PAYMENT_HISTORY', payload: response.data });
    } catch (error) {
      const errorMessage = error instanceof Error ? error.message : 'Failed to fetch payment history';
      console.error('Error fetching payment history:', error);
      dispatch({ type: 'SET_ERROR', payload: errorMessage });
    }
  }, []);

  /**
   * Clear error state
   */
  const clearError = useCallback((): void => {
    dispatch({ type: 'CLEAR_ERROR' });
  }, []);

  /**
   * Reset payment state (clear current payment)
   */
  const resetPayment = useCallback((): void => {
    dispatch({ type: 'RESET_PAYMENT' });
  }, []);

  // Memoized context value
  const value: PaymentContextType = useMemo(
    () => ({
      currentPayment: state.currentPayment,
      paymentHistory: state.paymentHistory,
      loading: state.loading,
      error: state.error,
      createTransaction,
      verifyTransaction,
      fetchPaymentHistory,
      clearError,
      resetPayment,
    }),
    [
      state.currentPayment,
      state.paymentHistory,
      state.loading,
      state.error,
      createTransaction,
      verifyTransaction,
      fetchPaymentHistory,
      clearError,
      resetPayment,
    ]
  );

  return <PaymentContext.Provider value={value}>{children}</PaymentContext.Provider>;
};

/**
 * usePayment Hook
 * Custom hook to access payment context
 *
 * @throws Error if used outside PaymentProvider
 * @returns PaymentContextType
 *
 * @example
 * ```tsx
 * const { createTransaction, verifyTransaction, currentPayment, loading } = usePayment();
 *
 * // Create transaction
 * const transactionRef = await createTransaction({
 *   amount: 50000,
 *   currency: Currency.COP,
 *   customer_email: 'user@example.com',
 *   customer_name: 'John Doe',
 * });
 *
 * // Verify transaction
 * const payment = await verifyTransaction(reference);
 * ```
 */
export const usePayment = (): PaymentContextType => {
  const context = useContext(PaymentContext);
  if (!context) {
    throw new Error('usePayment must be used within a PaymentProvider');
  }
  return context;
};
